import { getApiConfig } from '../config/api';
import { handleUnauthorized } from '../utils/jwtUtils';

// Get API configuration
const apiConfig = getApiConfig();

// Dummy API service for authentication
// This simulates a backend API that would handle database authentication

interface LoginRequest {
  // Wire field name matches the backend DTO. The UI still labels the form
  // field "Email" and stores it in formData.username locally; the call site
  // maps formData.username -> email when issuing the request.
  email: string;
  password: string;
  code?: string; // Optional 2FA code
  rememberMe?: boolean; // Optional remember me flag
}

interface SocialLoginRequest {
  loginType: 'google' | 'microsoft' | 'facebook' | 'apple' | 'uaepass';
  accessToken: string;
  email: string;
  name: string;
  picture?: string;
  firstName?: string;
  lastName?: string;
  userType?: number;
  timezone?: string;
}

interface MobileLoginRequest {
  mobileNumber: string;
  otp: string;
}

interface RegisterRequest {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  mobileNumber: string;
  password: string;
  confirmPassword: string;
  loginType: string;
  userType: number;
  timezone?: string;
}

interface ForgotPasswordRequest {
  email: string;
}

interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

interface AuthResponse {
  success: boolean;
  token?: string;
  refreshToken?: string;
  home?: string;
  expiresAt?: string;
  regionLock?: string | null;
  refreshTokenExpiresAt?: string;
  message?: string;
  user?: {
    userId: number;
    username: string;
    email: string;
    role: string;
    isActive: boolean;
    emailVerified: boolean;
    createdAt: string;
    userType: number;
    profilePictureUrl?: string;
    timezone?: string;
    aqiStandardId?: number;
    loginType?: string;
    hasPassword?: boolean;
  };
  // Legacy support for different response formats
  accessToken?: string;
  tokens?: {
    accessToken: string;
    refreshToken: string;
  };
}

// API base configuration
const API_BASE_URL = apiConfig.BASE_URL;

class ApiService {
  private baseURL: string;
  private isRefreshing = false;
  private failedQueue: Array<{
    endpoint: string;
    options: any;
    resolve: (_value: any) => void;
    reject: (_error: any) => void;
  }> = [];

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  // Process failed queue after token refresh - retry all queued requests
  private async processQueue(_error: any) {
    const queue = [...this.failedQueue];
    this.failedQueue = [];

    if (_error) {
      // Refresh failed - reject all queued requests
      queue.forEach(({ reject }) => {
        reject(_error);
      });
    } else {
      // Refresh succeeded - retry all queued requests with new token
      for (const { endpoint, options, resolve, reject } of queue) {
        try {
          const result = await this.request(endpoint, options);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }
    }
  }

  // Generic request method with automatic token refresh (Kaiterra-style)
  public async request<T>(
    endpoint: string,
    options: any = {}
  ): Promise<ApiResponse<T>> {
    try {
      // Add authorization header if token exists
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string> || {}),
      };

      // Add CSRF token for state-changing operations
      const isStateChangingOperation = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(options.method || 'GET');
      if (isStateChangingOperation) {
        const csrfToken = this.getCsrfToken();
        if (csrfToken) {
          headers['X-CSRF-Token'] = csrfToken;
        }
      }

      // Make actual HTTP request to .NET backend
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: options.method || 'GET',
        headers,
        body: options.body,
        credentials: 'include', // Include cookies for refresh token
        mode: 'cors', // Explicitly set CORS mode
      });

      // Handle 204 No Content responses (like DELETE operations)
      if (response.status === 204) {
        return {
          success: true,
          data: undefined,
          message: 'Operation completed successfully'
        };
      }

      let responseData;
      try {
        responseData = await response.json();
      } catch (error) {
        responseData = null;
      }

      // Handle different response formats from .NET backend
      if (response.ok) {
        return {
          success: true,
          data: responseData.data || responseData,
          message: responseData.message
        };
      }

      // Endpoints that run before a user has a session. A 401 on these is the
      // response itself (e.g. wrong password, unverified email) — not a token
      // expiry. Do not trigger refresh or redirect to /login.
      const preAuthEndpoints = [
        apiConfig.ENDPOINTS.LOGIN,
        apiConfig.ENDPOINTS.REGISTER,
        apiConfig.ENDPOINTS.FORGOT_PASSWORD,
        apiConfig.ENDPOINTS.RESET_PASSWORD,
        apiConfig.ENDPOINTS.REFRESH_TOKEN,
        apiConfig.ENDPOINTS.VERIFY_EMAIL,
        apiConfig.ENDPOINTS.RESEND_EMAIL_VERIFICATION,
        apiConfig.ENDPOINTS.CONFIRM_EMAIL_CHANGE,
        apiConfig.ENDPOINTS.SOCIAL_LOGIN,
        apiConfig.ENDPOINTS.SEND_OTP,
        apiConfig.ENDPOINTS.VERIFY_OTP,
      ];
      const isPreAuthCall = preAuthEndpoints.includes(endpoint);

      if (response.status === 401 && !isPreAuthCall) {
        // Unauthorized - try to refresh token once more
        if (!this.isRefreshing) {
          this.isRefreshing = true;

          try {
            const refreshResult = await this.refreshAccessToken();
            if (refreshResult.success) {
              // Refresh succeeded - retry all queued requests
              this.processQueue(null);
              // Retry the original request with new token
              return this.request(endpoint, options);
            } else {
              // Refresh failed - reject all queued requests
              this.processQueue(new Error('Refresh failed'));
              // Refresh failed, clear tokens and redirect to login
              // This handles account deletion from another device/session
              handleUnauthorized('API Service');
              return {
                success: false,
                message: 'Session expired. Please login again.',
                error: 'Session expired. Please login again.',
              };
            }
          } catch (error) {
            // Refresh failed - reject all queued requests
            this.processQueue(error);
            // Account deleted or token invalid - clear everything and redirect
            handleUnauthorized('API Service');
            return {
              success: false,
              message: 'Session expired. Please login again.',
              error: 'Authentication failed. Please login again.',
            };
          } finally {
            this.isRefreshing = false;
          }
        } else {
          // Another request is already refreshing, wait for it
          return new Promise((resolve, reject) => {
            this.failedQueue.push({ endpoint, options, resolve, reject });
          });
        }
      } else {
        return {
          success: false,
          error: responseData.message || responseData.error || `HTTP ${response.status}: ${response.statusText}`
        };
      }
    } catch (error) {
      return {
        success: false,
        error: `API connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  // Get CSRF token from meta tag or cookie
  private getCsrfToken(): string | null {
    try {
      // Try to get from meta tag first
      const metaTag = document.querySelector('meta[name="csrf-token"]');
      if (metaTag) {
        return metaTag.getAttribute('content');
      }

      // Fallback to cookie
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.startsWith('csrf-token=')) {
          return decodeURIComponent(cookie.substring('csrf-token='.length));
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  // Authentication methods
  // Helper method to extract and store authentication data
  // private handleAuthResponse(response: ApiResponse<AuthResponse>): ApiResponse<AuthResponse> {
  //   if (!response.success || !response.data) {
  //     return response;
  //   }

  //   const { data } = response;

  //   // Extract tokens from response (prioritize new structure)
  //   const accessToken = data.token || data.accessToken || (data.tokens?.accessToken);
  //   const refreshToken = data.refreshToken || (data.tokens?.refreshToken);

  //   if (!accessToken || !refreshToken) {
  //     return {
  //       success: false,
  //       error: 'Invalid response format: Authentication tokens are missing'
  //     };
  //   }

  //   // Validate token format
  //   if (!JWTManager.isValidTokenFormat(accessToken)) {
  //     return {
  //       success: false,
  //       error: 'Invalid token format received from server'
  //     };
  //   }

  //   // Store only essential tokens and expiration time
  //   JWTManager.setTokens({ accessToken, refreshToken });

  //   localStorage.setItem('expiresAt', data.expiresAt || '');
  //   localStorage.setItem('refreshTokenExpiresAt', data.refreshTokenExpiresAt || '');

  //   return response;
  // }

  async login(credentials: LoginRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await this.request<AuthResponse>(apiConfig.ENDPOINTS.LOGIN, {
      method: 'POST',
      body: JSON.stringify(credentials)
    });

    // If login is successful, handle auth response
    // if (response.success && response.data) {
    //   return this.handleAuthResponse(response);
    // }

    return response;
  }

  async register(userData: RegisterRequest): Promise<ApiResponse<AuthResponse>> {
    return this.request<AuthResponse>(apiConfig.ENDPOINTS.REGISTER, {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async forgotPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(apiConfig.ENDPOINTS.FORGOT_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(apiConfig.ENDPOINTS.RESET_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ resetToken: token, newPassword, confirmPassword })
    });
  }

  async verifyEmail(token: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(apiConfig.ENDPOINTS.VERIFY_EMAIL, {
      method: 'POST',
      body: JSON.stringify({ token })
    });
  }

  async resendEmailVerification(email: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(apiConfig.ENDPOINTS.RESEND_EMAIL_VERIFICATION, {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async confirmEmailChange(token: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(apiConfig.ENDPOINTS.CONFIRM_EMAIL_CHANGE, {
      method: 'POST',
      body: JSON.stringify({ token })
    });
  }

  async logout(): Promise<ApiResponse<{ message: string }>> {
    try {
      // Get refresh token to send to backend for revocation

      const response = await this.request<{ message: string }>(apiConfig.ENDPOINTS.LOGOUT, {
        method: 'POST'
      });

      // Clear tokens but keep saved credentials for remember me functionality
      localStorage.removeItem('userPreferences');
      localStorage.removeItem('userData');
      return response;
    } catch (error) {
      // Even if backend logout fails, clear local tokens but keep saved credentials
      localStorage.removeItem('userPreferences');
      localStorage.removeItem('userData');
      // localStorage.removeItem('deviceTableColumnState');
      // localStorage.removeItem('liveDataReportColumnState');
      // localStorage.removeItem('userSelectedColumnsForDashboard');
      return {
        success: true,
        message: 'Logged out successfully (local cleanup completed)'
      };
    }
  }

  async deleteAccount(
    userId: number,
    currentPassword?: string,
  ): Promise<ApiResponse<{ message: string; devicesUpdated?: number }>> {
    try {
      const response = await this.request<{ message: string; devicesUpdated?: number }>(
        `${apiConfig.ENDPOINTS.DELETE_ACCOUNT}/${userId}`,
        {
          method: 'DELETE',
          body: currentPassword ? JSON.stringify({ currentPassword }) : undefined,
        }
      );

      // If deletion is successful, clear all local data
      if (response.success) {
        localStorage.clear();
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: `Account deletion failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  private refreshPromise: Promise<ApiResponse<any>> | null = null;

  async refreshAccessToken(): Promise<ApiResponse<any>> {
    // Coalesce concurrent refresh calls so parallel 401s don't each rotate the
    // refresh-token cookie and race each other — only the first would win and
    // the rest would 401, falsely triggering a logout.
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const url = apiConfig.BASE_URL + apiConfig.ENDPOINTS.REFRESH_TOKEN;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
        });

        return {
          success: response.ok,
          data: response.ok ? await response.json().catch(() => null) : null,
          message: response.ok ? 'Token refreshed successfully' : 'Failed to refresh token'
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Refresh failed'
        };
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  // Social media authentication methods
  async socialLogin(socialData: SocialLoginRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await this.request<AuthResponse>(apiConfig.ENDPOINTS.SOCIAL_LOGIN, {
      method: 'POST',
      body: JSON.stringify(socialData)
    });

    // If social login is successful, handle auth response
    // if (response.success && response.data) {
    //   return this.handleAuthResponse(response);
    // }

    return response;
  }

  // Mobile authentication methods
  async sendMobileOTP(mobileNumber: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(apiConfig.ENDPOINTS.SEND_OTP, {
      method: 'POST',
      body: JSON.stringify({ mobileNumber })
    });
  }

  async verifyMobileOTP(mobileData: MobileLoginRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await this.request<AuthResponse>(apiConfig.ENDPOINTS.VERIFY_OTP, {
      method: 'POST',
      body: JSON.stringify(mobileData)
    });

    return response;
  }

  // Get current user info
  // getCurrentUser() {
  //   const accessToken = JWTManager.getAccessToken();
  //   if (!accessToken) return null;

  //   // Check if token is expired
  //   if (JWTManager.isTokenExpired(accessToken)) {
  //     JWTManager.clearTokens();
  //     return null;
  //   }

  //   // Try to get user from localStorage first (new structure)
  //   const userData = localStorage.getItem('userData');

  //   if (userData) {
  //     try {
  //       const parsedUser = JSON.parse(userData);
  //       // Ensure we have valid user data with new structure
  //       if (parsedUser && parsedUser.username && parsedUser.userId) {
  //         return parsedUser;
  //       }
  //     } catch (error) {
  //       console.error('API Service - Error parsing user data:', error);
  //     }
  //   }

  //   // Fallback: Try to get user from token
  //   const userFromToken = JWTManager.getUserFromToken(accessToken);

  //   if (userFromToken && userFromToken.username !== 'unknown') {
  //     // Convert legacy user structure to new structure
  //     return {
  //       userId: parseInt(userFromToken.id) || 0,
  //       username: userFromToken.username,
  //       email: userFromToken.email,
  //       isActive: true,
  //       emailVerified: true,
  //       createdAt: new Date().toISOString(),
  //       userType: 1
  //     };
  //   }

  //   return null;
  // }

  // Check is authenticated or not
  async checkIsAuthenticated(): Promise<any> {
    const response = await this.request<any>(apiConfig.ENDPOINTS.IS_AUTHENTICATED, {
      method: 'GET',
    });

    // If login is successful, handle auth response
    // if (response.success && response.data) {
    //   return this.handleAuthResponse(response);
    // }

    return response;
  }
}

// Create and export API instance
export const apiService = new ApiService(API_BASE_URL);

// Export types for use in components
export type {
  LoginRequest,
  SocialLoginRequest,
  MobileLoginRequest,
  RegisterRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ApiResponse,
  AuthResponse
};